import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Autocomplete,
  Button,
  Card,
  IconButton,
  Switch,
  FormControlLabel,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InfoIcon from "@mui/icons-material/Info";
import ListAltIcon from "@mui/icons-material/ListAlt";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import api from "../../../api/client";
import Header from "../anna-university-template/Header";
import { instructionsToText, instructionsToList } from "./paperBuilder";
import "../anna-university-template/template.css";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { OverAllLoader } from "components/OverAllLoader";
import { useChangeableItems, getLabelByKey } from "../../../store/useDynamicLabel";

const STATUS_OPTIONS = ["ACTIVE", "INACTIVE"];
const PATTERN_OPTIONS = ["list", "either-or"];
const DIVISION_OPTIONS = ["any", "per-group"];
const patternLabel = (p) => (p === "list" ? "List" : "Either-Or");
const divisionLabel = (d) => (d === "per-group" ? "Choice per group" : "Any N from total");

// College name is bound from the logged-in user's institute (localStorage) and shown disabled.
const COLLEGE_NAME = localStorage.getItem("CollegeName") || "";

// Template-level header constants only. Subject/program/date/marks etc. are resolved
// per-paper at SaveQuestionPaper, so they are not part of the template.
// optional → no asterisk + not required; disabled → auto-bound from localStorage, not editable.
const emptyDraft = (index, start) => ({
  key: `part${String.fromCharCode(65 + index)}`,
  title: `PART ${String.fromCharCode(65 + index)}`,
  pattern: "list",
  count: 10,
  marks: 2,
  start: start || "",
  splitMark: [],
  instructions: "Answer ALL questions",
  hasChoice: false,
  choice: { total_given: "", must_answer: "", division: "any", group_size: "", group_answer: "" },
});

// Project-standard field style (matches CreateQuestionPaper).
const commonTextFieldStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "white" },
    "&:hover fieldset": { borderColor: "#d1d1d1" },
    "&.Mui-focused fieldset": { borderColor: "#1c97e3" },
  },
};
// Same as commonTextFieldStyle but keeps a visible default border (used in the Add/Edit Part popup).
const modalFieldStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#d1d1d1" },
    "&:hover fieldset": { borderColor: "#a8a8a8" },
    "&.Mui-focused fieldset": { borderColor: "#1c97e3" },
  },
};
const fieldBoxSx = { width: { xs: "100%", md: "calc(50% - 20px)" } };

const partTotal = (p) => (p.choice ? Number(p.choice.must_answer) || 0 : Number(p.count) || 0) * (Number(p.marks) || 0);
const marksLine = (p) => {
  const per = p.choice ? Number(p.choice.must_answer) || 0 : Number(p.count) || 0;
  return `(${per} × ${p.marks} = ${partTotal(p)} Marks)`;
};
const choiceText = (c) =>
  c.division === "per-group"
    ? `Answer ${c.group_answer} out of every ${c.group_size} questions`
    : `Answer any ${c.must_answer} out of ${c.total_given}`;

const ROMAN = ["i", "ii", "iii", "iv", "v", "vi", "vii", "viii", "ix", "x", "xi", "xii"];
const romanLabel = (i) => ROMAN[i] || `${i + 1}`;

const TemplateCreate = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const HEADER_FIELDS = [
    { key: "exam_title", label: `${getLabel('EXAM')} Title`, ph: "Model Examination", max: 100 },
    { key: "college_name", label: `${getLabel('COLLEGE_NAME')} / ${getLabel('INSTITUTE')}`, ph: "College of Engineering", disabled: true },
    { key: "university", label: `${getLabel('UNIVERSITY_NAME')}`, ph: `Enter ${getLabel('UNIVERSITY_NAME')}`, optional: true },
    { key: "footer_note", label: "Footer Note", ph: "All the Best!", optional: true },
  ];

  const emptyHeader = () => ({
    ...HEADER_FIELDS.reduce(
      (a, f) => ({ ...a, [f.key]: f.key === "college_name" ? COLLEGE_NAME : "" }),
      {}
    ),
    instructions: "",
  });

  const textRule = (label, { required = true, min = 3, max = 50 } = {}) => {
    const base = Yup.string().trim();
    return (required ? base.required(`${label} is required`) : base.notRequired())
      .test("not-only-numbers", `${label} cannot contain only numbers.`, (v) => !v || !/^\d+$/.test(v))
      .test("not-only-special-characters", `${label} cannot contain only special characters.`, (v) => !v || /[A-Za-z0-9]/.test(v))
      .test("min-length", `${label} must be at least ${min} characters long.`, (v) => !v || v.length >= min)
      .test("max-length", `${label} cannot exceed ${max} characters.`, (v) => !v || v.length <= max);
  };
  // Header fields that use textRule (others keep the plain required check)
  const TEXT_RULE_FIELDS = ["exam_title", "footer_note"];

  // template_name + status required; required header fields (not optional/disabled) validated too.
  const schema = Yup.object().shape({
    templateName: textRule(`${getLabel('TEMPLATE')} name`),
    status: Yup.string().trim().required("Status is required"),
    templateType: Yup.string()
      .trim()
      .required(`${getLabel('TEMPLATE')} Type is required`),

    templateFile: Yup.mixed()
      .nullable()
      .when(["templateType", "sourceFilePath"], {
        is: (type, path) => type === "EXTRACTED" && !path,
        then: (schema) =>
          schema.required(`${getLabel('TEMPLATE')} document is required`),
        otherwise: (schema) =>
          schema.nullable().notRequired(),
      }),

    header: Yup.object().shape(
      HEADER_FIELDS.reduce(
        (acc, f) =>
          f.disabled
            ? acc
            : TEXT_RULE_FIELDS.includes(f.key)
              ? { ...acc, [f.key]: textRule(f.label, { required: !f.optional, ...(f.max ? { max: f.max } : {}) }) }
              : f.optional
                ? acc
                : { ...acc, [f.key]: Yup.string().trim().required(`${f.label} is required`) },
        {
          instructions: Yup.string()
            .trim()
            .required("Instructions are required")
            .test("instruction-lines", "", function (value) {
              const lines = instructionsToList(value);
              for (let i = 0; i < lines.length; i++) {
                const line = lines[i].trim();
                if (!line) continue;
                const label = `Instruction #${i + 1}`;
                if (/^\d+$/.test(line)) return this.createError({ message: `${label} cannot contain numbers only.` });
                if (!/[A-Za-z]/.test(line)) return this.createError({ message: `${label} must contain letters.` });
                if (line.length < 3) return this.createError({ message: `${label} must be at least 3 characters long.` });
              }
              return true;
            }),
        }
      )
    ),
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();

  const isViewMode = location.state?.view || false;
  const isEditMode = location.state?.edit || false;

  const [templateId, setTemplateId] = useState(location.state?.template_id || null);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [draft, setDraft] = useState(emptyDraft(0));
  const [uploadingTemplate, setUploadingTemplate] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      templateName: "",
      status: "ACTIVE",
      header: emptyHeader(),
      templateType: "MANUAL",
      templateFile: null,
      sourceFilePath: "",
    },
    mode: "onChange",
  });

  // Live values used by the read-only preview.
  const header = watch("header") || emptyHeader();
  const templateType = watch("templateType");
  const templateFile = watch("templateFile");

  const hydrate = (t) => {
    if (!t) return;
    setTemplateId(t.template_id || null);
    setValue("templateName", t.template_name || t.name || "");
    setValue("status", t.status || "ACTIVE");
    setValue("templateType", t.template_type || "MANUAL");
    setValue("sourceFilePath", t.source_file_path || "");
    // College name always stays bound to the logged-in institute.
    setValue("header", {
      ...emptyHeader(),
      ...(t.header_defaults || {}),
      instructions: instructionsToText(t.header_defaults?.instructions),
      college_name: COLLEGE_NAME,
    });
    setParts(
      (t.parts || []).map((p) => ({
        key: p.partkey || p.key,
        title: p.title,
        pattern: p.pattern,
        count: p.count,
        marks: p.marks,
        start: p.start ?? "",
        splitMark: p.split_mark || [],
        instructions: Array.isArray(p.instructions) ? p.instructions : p.instructions ? [p.instructions] : [],
        choice: p.choice || null,
      }))
    );
  };

  const loadTemplate = async (id) => {
    try {
      const res = await api.post("GetPaperTemplate", { template_id: id });
      if (res?.data?.status === "success" && res?.data?.data) hydrate(res.data.data);
    } catch (e) {
      console.log("Error fetching template:", e);
    }
  };

  useEffect(() => {
    if (location.state?.template?.parts?.length) hydrate(location.state.template);
    else if (location.state?.template_id) loadTemplate(location.state.template_id);
    else if (location.state?.template) hydrate(location.state.template);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nextStart = () => {
    if (!parts.length) return 1;
    const last = parts[parts.length - 1];
    const lastStart = Number(last.start) || 1;
    const span = last.choice ? Number(last.choice.total_given) || last.count : last.count;
    return lastStart + Number(span || 0);
  };

  const openAddPart = () => {
    setEditingIndex(null);
    setDraft(emptyDraft(parts.length, nextStart()));
    setModalOpen(true);
  };

  const openEditPart = (idx) => {
    const p = parts[idx];
    setEditingIndex(idx);
    setDraft({
      key: p.key,
      title: p.title,
      pattern: p.pattern,
      count: p.count,
      marks: p.marks,
      start: p.start ?? "",
      splitMark: p.splitMark || [],
      instructions: (p.instructions || []).join("\n"),
      hasChoice: !!p.choice,
      choice: {
        total_given: p.choice?.total_given ?? "",
        must_answer: p.choice?.must_answer ?? "",
        division: p.choice?.division || "any",
        group_size: p.choice?.group_size ?? "",
        group_answer: p.choice?.group_answer ?? "",
      },
    });
    setModalOpen(true);
  };

  const draftToPart = (d) => ({
    key: d.key,
    title: d.title,
    pattern: d.pattern,
    count: Number(d.count) || 0,
    marks: Number(d.marks) || 0,
    splitMark: (d.splitMark || []).map(Number),
    ...(d.start !== "" && d.start != null ? { start: Number(d.start) } : { start: "" }),
    instructions: (d.instructions || "").split("\n").map((s) => s.trim()).filter(Boolean),
    choice: d.hasChoice
      ? {
        total_given: Number(d.choice.total_given) || 0,
        must_answer: Number(d.choice.must_answer) || 0,
        division: d.choice.division,
        ...(d.choice.division === "per-group"
          ? { group_size: Number(d.choice.group_size) || 0, group_answer: Number(d.choice.group_answer) || 0 }
          : {}),
      }
      : null,
  });

  const textError = (label, value, { required = true, min = 3 } = {}) => {
    const v = (value || "").trim();
    if (!v) return required ? `${label} is required` : "";
    if (/^\d+$/.test(v)) return `${label} cannot contain only numbers.`;
    if (!/[A-Za-z0-9]/.test(v)) return `${label} cannot contain only special characters.`;
    if (v.length < min) return `${label} must be at least ${min} characters long.`;
    return "";
  };

  const perGroupMustAnswer = (d) => {
    const count = Number(d.count) || 0;
    const size = Number(d.choice.group_size) || 0;
    const answer = Number(d.choice.group_answer) || 0;
    return size > 0 && count % size === 0 ? (count / size) * answer : "";
  };

  const getDraftErrors = (d) => {
    const errors = {};
    const title = textError("Title", d.title);
    if (title) errors.title = title;
    if (Number(d.marks) > 100) errors.marks = "Mark per question cannot exceed 100.";
    const badLine = (d.instructions || "")
      .split("\n")
      .map((line) => textError("Instruction", line, { required: false }))
      .find(Boolean);
    if (badLine) errors.instructions = badLine;
    if (d.hasChoice) {
      const notPositive = (v) => v !== "" && v != null && !(Number.isInteger(Number(v)) && Number(v) >= 1);
      if (d.choice.division === "per-group") {
        const size = Number(d.choice.group_size) || 0;
        if (notPositive(d.choice.group_size)) {
          errors.group_size = "Questions per Group must be greater than 0.";
        } else if (size > 0 && (Number(d.count) || 0) % size !== 0) {
          errors.group_size = "Questions per Group must evenly divide the total number of questions.";
        }
        if (notPositive(d.choice.group_answer)) {
          errors.group_answer = "Answer per Group must be greater than 0.";
        }
      } else if (notPositive(d.choice.must_answer)) {
        errors.must_answer = "Must Answer must be greater than 0.";
      } else if (
        d.choice.must_answer !== "" &&
        Number(d.choice.must_answer) >= Number(d.choice.total_given)
      ) {
        errors.must_answer = "Must Answer must be less than Total Given.";
      }
    }
    return errors;
  };
  const draftErrors = getDraftErrors(draft);

  const savePart = () => {
    if (Object.keys(draftErrors).length) return showSnackbar("error", "Please fix the highlighted fields");
    if (!draft.key.trim() || !draft.title.trim()) return showSnackbar("error", "Part key and title are required");
    if (!Number(draft.count)) return showSnackbar("error", "Count must be greater than 0");
    if (!Number(draft.marks)) return showSnackbar("error", "Marks must be greater than 0");
    if (draft.splitMark?.length) {
      const splitSum = draft.splitMark.reduce((s, m) => s + Number(m), 0);
      if (splitSum !== Number(draft.marks)) {
        return showSnackbar(
          "error",
          `Split marks must total ${draft.marks} (currently ${splitSum})`
        );
      }
    }
    if (
      draft.hasChoice &&
      draft.choice.division !== "per-group" &&
      Number(draft.choice.must_answer) > Number(draft.choice.total_given)
    ) {
      return showSnackbar("error", "Must Answer cannot exceed Total Given");
    }
    const part = draftToPart(
      draft.hasChoice && draft.choice.division === "per-group"
        ? { ...draft, choice: { ...draft.choice, must_answer: perGroupMustAnswer(draft) } }
        : draft
    );
    setParts((prev) => (editingIndex == null ? [...prev, part] : prev.map((p, i) => (i === editingIndex ? part : p))));
    setModalOpen(false);
  };

  const movePart = (idx, dir) =>
    setParts((prev) => {
      const j = idx + dir;
      if (j < 0 || j >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });
  const deletePart = (idx) => setParts((prev) => prev.filter((_, i) => i !== idx));


  const buildPayload = (data) => {
    const header_defaults = {};
    Object.keys(data.header).forEach((k) => {
      const v = data.header[k];
      if (k === "instructions") {
        const list = instructionsToList(v);
        if (list.length) header_defaults.instructions = list;
      } else if (v !== "" && v != null) header_defaults[k] = v;
    });
    return {
      ...(templateId ? { template_id: templateId } : {}),
      template_name: data.templateName.trim(),
      status: data.status,
      header_defaults,
      template_type: data?.templateType,
      source_file_path: data?.sourceFilePath,
      parts: parts.map((p) => ({
        partkey: p.key,
        title: p.title,
        pattern: p.pattern,
        count: Number(p.count) || 0,
        marks: Number(p.marks) || 0,
        ...(p.start !== "" && p.start != null ? { start: Number(p.start) } : {}),
        split_mark: (p.splitMark || []).map(Number),
        pick: {},
        ...(p.instructions?.length ? { instructions: p.instructions } : {}),
        ...(p.choice ? { choice: p.choice } : {}),
      })),
    };
  };

  // Parts live outside react-hook-form, so they are validated here on submit.
  const validateParts = () => {
    if (!parts.length) return "Add at least one part";
    for (let i = 0; i < parts.length; i++) {
      const p = parts[i];
      if (p.choice && Number(p.choice.must_answer) > Number(p.choice.total_given))
        return `Part ${i + 1}: must_answer cannot exceed total_given`;
    }
    return null;
  };

  const handleTemplateUpload = async (file) => {
    if (!file) return;

    setUploadingTemplate(true);

    try {
      const formData = new FormData();
      formData.append("files", file);

      const response = await api.post("ExtractPaperTemplate", formData);

      if (response?.data?.status !== "success") {
        showSnackbar(
          "error",
          response?.data?.message || "Unable to extract the file"
        );
        return;
      }

      const extractedData = response?.data?.data;

      const sourceFile = extractedData?.source_files?.[0];

      if (!extractedData) {
        showSnackbar("error", "No template data was extracted");
        return;
      }

      const headerDefaults = extractedData?.header_defaults || {};

      setValue("templateFile", file, {
        shouldValidate: true,
        shouldDirty: true,
      });

      setValue("sourceFilePath", sourceFile?.source_file_path || "", {
        shouldValidate: true,
        shouldDirty: true,
      });

      reset({
        status: "ACTIVE",
        templateType: "EXTRACTED",
        templateFile: file,
        sourceFilePath: sourceFile?.source_file_path || "",

        header: {
          ...emptyHeader(),

          exam_title: headerDefaults.title || "",
          college_name: COLLEGE_NAME,
          programme: headerDefaults.programme || "",
          course_title: headerDefaults.course_title || "",
          course_code: headerDefaults.course_code || "",
          semester: headerDefaults.semester || "",
          slot: headerDefaults.slot || "",
          max_marks: headerDefaults.max_marks || "",
          duration: headerDefaults.duration || "",
          footer_note: "",
          university: headerDefaults.institute_name || "",
          instructions: instructionsToText(headerDefaults.instructions),
        },
      });

      setParts(
        (extractedData.parts || []).map((p, index) => ({
          key: p.partkey || p.key || `part${index}`,
          title: p.title || "",
          pattern: p.pattern || "list",
          count: Number(p.count) || 0,
          marks: Number(p.marks) || 0,
          start: p.start ?? "",
          splitMark: p.split_mark || [],
          instructions: Array.isArray(p.instructions)
            ? p.instructions
            : p.instructions
              ? [p.instructions]
              : [],
          choice: p.choice || null,
        }))
      );

      showSnackbar(
        "success",
        response?.data?.message || `${getLabel('TEMPLATE')} extracted successfully`
      );
    } catch (error) {
      console.error("Error extracting template:", error);

      setValue("templateFile", null, {
        shouldValidate: true,
        shouldDirty: true,
      });

      setValue("sourceFilePath", "", {
        shouldValidate: true,
        shouldDirty: true,
      });

      showSnackbar("error", "Failed to extract the file, please try again later");
    } finally {
      setUploadingTemplate(false);
    }
  };

  const onSubmit = async (data) => {
    const partErr = validateParts();
    if (partErr) return showSnackbar("error", partErr);
    setLoading(true);
    try {
      const endpoint = templateId ? "UpdatePaperTemplate" : "CreatePaperTemplate";
      const res = await api.post(endpoint, buildPayload(data));
      if (res?.data?.status === "success") {
        showSnackbar("success", templateId ? `${getLabel('TEMPLATE')} updated` : `${getLabel('TEMPLATE')} saved`);
        navigate("/templates");
      } else {
        showSnackbar("error", res?.data?.message || "Failed to save template");
      }
    } catch (e) {
      console.log("Error saving template:", e);
      showSnackbar("error", e?.response?.data?.message || "Failed to save template");
    } finally {
      setLoading(false);
    }
  };

  const totalMarks = parts.reduce((s, p) => s + partTotal(p), 0);

  // Same header as the export / generate / view paper (anna-university-template/Header).
  // Per-paper fields (department, subject, time) are resolved at SaveQuestionPaper, so
  // they stay blank here and Header hides those rows; Time stays labelled to keep the
  // Time / Maximum Marks row aligned exactly like the export.
  const previewHeader = {
    university: header.university,
    examTitle: header.exam_title,
    collegeName: header.college_name,
    department: header.department,
    subjectCode: header.subject_code,
    subjectName: header.subject_name,
    semester: header.semester,
    regulation: header.regulation,
    academicYear: header.academic_year,
    time: header.time,
    maxMarks: totalMarks,
  };
  const cardSx = { p: 2, borderRadius: "12px", border: "1px solid #f1f3f6", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" };
  const setChoice = (patch) => setDraft((d) => ({ ...d, choice: { ...d.choice, ...patch } }));

  // Split-mark is built up one value at a time so the same mark can repeat
  // (e.g. 2 marks → 1 + 1, or 10 marks → 5 + 5). The dropdown only offers the
  // remaining marks, so the running total never exceeds Marks/Question.
  const splitSum = (draft.splitMark || []).reduce((s, m) => s + Number(m), 0);
  const splitRemaining = Math.max(0, (Number(draft.marks) || 0) - splitSum);
  // Capped at 100 (the max Mark per Question): a large typed value like 10000 must not
  // build thousands of menu items — that froze the whole page.
  const splitAddOptions = Array.from({ length: Math.min(splitRemaining, 100) }, (_, i) => i + 1);
  const splitMismatch = (draft.splitMark || []).length > 0 && splitSum !== Number(draft.marks);

  // Preview sub-parts when a question's marks are split (e.g. 13 → i) (6), ii) (7)).
  const splitLines = (splitMark, indent) =>
    splitMark.map((sm, si) => (
      <Box key={si} sx={{ display: "flex", justifyContent: "space-between", pl: indent, fontSize: "11px" }}>
        <span>{romanLabel(si)}) ____________________</span>
        <span>({sm})</span>
      </Box>
    ));

  // Canonical section header (icon + title), matching the project's other form screens.
  const SectionHeader = ({ icon, title, action }) => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {icon}
        <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>{title}</Typography>
      </Box>
      {action}
    </Box>
  );

  const renderPreview = () => (
    <Box
      sx={{
        position: "relative", overflow: "hidden", background: "#fff", border: "1px solid #e2e8f0",
        borderRadius: "8px", p: 2.5, fontFamily: '"Times New Roman", Georgia, serif', color: "#000",
        fontSize: "12px", lineHeight: 1.5, minHeight: 300,
      }}
    >
      <Box sx={{ position: "absolute", top: "45%", left: "50%", transform: "translate(-50%,-50%) rotate(-30deg)", fontFamily: "Arial", fontWeight: 800, fontSize: "52px", letterSpacing: "6px", color: "rgba(28,151,227,.07)", whiteSpace: "nowrap", pointerEvents: "none" }}>
        TALENT IQ
      </Box>
      {/* Same header component as the export / generate / view paper. */}
      <Box sx={{ position: "relative" }}>
        <Header data={previewHeader} />
      </Box>

      {!!header.instructions?.trim() && (
        <Box sx={{ position: "relative", mb: 1.5 }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 700 }}>General Instruction(s):</Typography>
          <Typography sx={{ fontSize: "11px", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {header.instructions}
          </Typography>
        </Box>
      )}

      {parts.length === 0 && <Typography sx={{ color: "#94a3b8", textAlign: "center", py: 3 }}>Add parts to see the preview.</Typography>}

      {parts.map((p, i) => {
        const start = Number(p.start) || 1;
        const isList = p.pattern === "list";
        const showCount = Math.min(p.count, isList ? 8 : 3);
        const remaining = p.count - showCount;
        return (
          <Box key={i} sx={{ position: "relative", mb: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #000", fontWeight: 700, fontSize: "12px" }}>
              <span>{p.title}</span>
              <span>{marksLine(p)}</span>
            </Box>
            {!!(p.instructions || []).length && (
              <Typography sx={{ fontSize: "10px", fontStyle: "italic", color: "#333" }}>· {(p.instructions || []).join(" · ")}</Typography>
            )}
            {p.choice && <Typography sx={{ fontSize: "10.5px", color: "#b45309", fontWeight: 600 }}>[ {choiceText(p.choice)} ]</Typography>}
            {(() => {
              const hasSplit = p.splitMark?.length > 0;
              return isList
                ? Array.from({ length: showCount }).map((_, k) =>
                  hasSplit ? (
                    <Box key={k} sx={{ mb: 0.5 }}>
                      <Typography sx={{ fontSize: "11px" }}>{start + k}.</Typography>
                      {splitLines(p.splitMark, 2.5)}
                    </Box>
                  ) : (
                    <Typography key={k} sx={{ fontSize: "11px" }}>{start + k}. ____________________________  ({p.marks})</Typography>
                  )
                )
                : Array.from({ length: showCount }).map((_, k) => (
                  <Box key={k} sx={{ mb: 0.3 }}>
                    {hasSplit ? (
                      <>
                        <Typography sx={{ fontSize: "11px" }}>{start + k}. a)</Typography>
                        {splitLines(p.splitMark, 3)}
                      </>
                    ) : (
                      <Typography sx={{ fontSize: "11px" }}>{start + k}. a) ____________________  ({p.marks})</Typography>
                    )}
                    <Typography sx={{ fontSize: "10px", textAlign: "center", fontStyle: "italic" }}>(or)</Typography>
                    {hasSplit ? (
                      <>
                        <Typography sx={{ fontSize: "11px" }}>&nbsp;&nbsp;&nbsp;&nbsp;b)</Typography>
                        {splitLines(p.splitMark, 3)}
                      </>
                    ) : (
                      <Typography sx={{ fontSize: "11px" }}>&nbsp;&nbsp;&nbsp;&nbsp;b) ____________________  ({p.marks})</Typography>
                    )}
                  </Box>
                ));
            })()}
            {remaining > 0 && (
              <Typography sx={{ fontSize: "10px", color: "#94a3b8" }}>... {remaining} more {isList ? "" : "either-or"}</Typography>
            )}
          </Box>
        );
      })}

      {(parts.length > 0 || !!header.footer_note?.trim()) && (
        <Box sx={{ position: "relative", textAlign: "center", mt: 1 }}>
          <Typography sx={{ fontSize: "11px" }}>──── {header.footer_note || "All the Best!"} ────</Typography>
          {parts.length > 0 && (
            <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "1px" }}>— {`End of ${getLabel('QUESTION_PAPER')}`} —</Typography>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {uploadingTemplate && (
        <OverAllLoader message="Extracting template..." />
      )}
      {/* Back Button */}
      <Grid
        sx={{ display: "flex", alignItems: "center", color: "#1c97e3", gap: "4px", cursor: "pointer", width: "40px", mb: 2 }}
        onClick={() => navigate("/templates")}
      >
        <ArrowBackIcon sx={{ fontSize: "17px" }} />
        <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>Back</Typography>
      </Grid>

      {/* Page header */}
      <Grid sx={{ pb: 2, display: "flex", gap: 1, alignItems: "center", borderBottom: "1px solid #d1d1d1" }}>
        <Grid sx={{ backgroundColor: "#1c97e3", p: 0.5, borderRadius: "10px", display: "inline-flex" }}>
          <DashboardCustomizeIcon sx={{ color: "white", fontSize: "35px" }} />
        </Grid>
        <Typography sx={{ fontSize: "30px", fontWeight: "bold" }}>
          {isViewMode ? `View ${getLabel('TEMPLATE')}` : isEditMode ? `Update ${getLabel('TEMPLATE')}` : `Create ${getLabel('TEMPLATE')}`}
        </Typography>
      </Grid>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Two columns */}
        <Box
          sx={{
            display: "flex", gap: 2, flexWrap: "wrap", mt: 3, alignItems: "flex-start",
            pointerEvents: isViewMode ? "none" : "auto", opacity: isViewMode ? 0.95 : 1,
          }}
        >
          {/* LEFT */}
          <Box sx={{ flex: "1 1 560px", minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Template info (moved to top) */}
            <Card sx={cardSx}>
              <SectionHeader
                icon={<InfoIcon sx={{ color: "#1c97e3", fontSize: "28px" }} />}
                title={`${getLabel('TEMPLATE')} Info`}
              />
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {/* Template Type (create only) */}
                {!isEditMode && (
                <Grid sx={{ ...fieldBoxSx, minWidth: "280px" }}>
                  <Typography>
                    {`${getLabel('TEMPLATE')} Type`} <span style={{ color: "red" }}>*</span>
                  </Typography>

                  <Controller
                    name="templateType"
                    control={control}
                    defaultValue="MANUAL"
                    render={({ field }) => (
                      <RadioGroup
                        row
                        {...field}
                        sx={{
                          gap: 2,
                          flexWrap: "nowrap",
                          mt: 0.5,
                        }}
                        onChange={(e) => {
                          const value = e.target.value;

                          field.onChange(value);

                          if (value === "MANUAL") {
                            setValue("templateFile", null);
                            setValue("sourceFilePath", "");
                          }
                        }}
                      >
                        <FormControlLabel
                          value="MANUAL"
                          control={<Radio size="small" />}
                          label="Create New"
                        />

                        <FormControlLabel
                          value="EXTRACTED"
                          control={<Radio size="small" />}
                          label="Upload Reference Template"
                        />
                      </RadioGroup>
                    )}
                  />
                </Grid>
                )}

                <Grid sx={fieldBoxSx}>
                  <Typography>{`${getLabel('TEMPLATE')} Name`} <span style={{ color: "red" }}>*</span></Typography>
                  <Controller
                    name="templateName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder={`${getLabel('TEMPLATE')} name`}
                        error={!!errors.templateName}
                        helperText={errors.templateName?.message}
                        sx={commonTextFieldStyle}
                      />
                    )}
                  />
                </Grid>
              </Box>

              {templateType === "EXTRACTED" && !isEditMode && (
                <Box sx={{ mt: 1 }}>
                  <Typography sx={{ mb: 1 }}>
                    Upload template
                    <span style={{ color: "red" }}> *</span>
                  </Typography>

                  <Button
                    component="label"
                    variant="outlined"
                    disableRipple
                    fullWidth
                    sx={{
                      minHeight: "120px",
                      borderRadius: "12px",
                      border: "1.5px dashed #b8c4d6",
                      backgroundColor: "#f8fbff",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.8,
                      textTransform: "none",
                      color: "#333",
                      "&:hover": {
                        backgroundColor: "#f1f7ff",
                        borderColor: "#1c97e3",
                      },
                    }}
                  >
                    <CloudUploadOutlinedIcon
                      sx={{
                        fontSize: "32px",
                        color: "#1c97e3",
                      }}
                    />

                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#26364a",
                      }}
                    >
                      {`Upload Existing ${getLabel('TEMPLATE')}`}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#8a97a8",
                      }}
                    >
                      Click to choose a document or image
                    </Typography>

                    <input
                      type="file"
                      hidden
                      accept=".pdf,.doc,.docx,image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;

                        if (file) {
                          handleTemplateUpload(file);
                        }

                        // Allows selecting the same file again
                        e.target.value = "";
                      }}
                    />
                  </Button>

                  {/* Selected file */}
                  {templateFile && (
                    <Box
                      sx={{
                        mt: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        px: 2,
                        py: 1.5,
                        border: "1px solid #e1e7ef",
                        borderRadius: "10px",
                        backgroundColor: "#fafcff",
                      }}
                    >
                      <DescriptionOutlinedIcon
                        sx={{
                          color: "#1c97e3",
                          fontSize: "24px",
                        }}
                      />

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#26364a",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {templateFile.name}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: "11px",
                            color: "#8a97a8",
                            mt: 0.25,
                          }}
                        >
                          {(templateFile.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Validation error */}
                  {errors.templateFile && (
                    <Typography
                      sx={{
                        color: "#d32f2f",
                        fontSize: "12px",
                        mt: 0.75,
                        ml: 1.5,
                      }}
                    >
                      {errors.templateFile.message}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Status is shown only when editing/viewing; create defaults to ACTIVE. */}
              {(isEditMode || isViewMode) && (
                <Grid sx={[fieldBoxSx, { marginTop: 3 }]}>
                  <Typography>Status <span style={{ color: "red" }}>*</span></Typography>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        options={STATUS_OPTIONS}
                        value={field.value || null}
                        disableClearable
                        onChange={(_, v) => field.onChange(v || "")}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select status"
                            error={!!errors.status}
                            helperText={errors.status?.message}
                            sx={commonTextFieldStyle}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
              )}
            </Card>

            {/* Header details */}
            <Card sx={cardSx}>
              <SectionHeader
                icon={<DescriptionOutlinedIcon sx={{ color: "#1c97e3", fontSize: "28px" }} />}
                title="Header Details"
              />
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {HEADER_FIELDS.map((f) => {
                  const required = !f.optional && !f.disabled;
                  return (
                    <Grid key={f.key} sx={fieldBoxSx}>
                      <Typography>
                        {f.label} {required && <span style={{ color: "red" }}>*</span>}
                      </Typography>
                      <Controller
                        name={`header.${f.key}`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            disabled={f.disabled}
                            placeholder={f.ph}
                            error={!!errors.header?.[f.key]}
                            helperText={errors.header?.[f.key]?.message}
                            sx={commonTextFieldStyle}
                          />
                        )}
                      />
                    </Grid>
                  );
                })}
              </Box>
            </Card>

            <Card sx={cardSx}>
              <SectionHeader
                icon={<FormatListNumberedIcon sx={{ color: "#1c97e3", fontSize: "28px" }} />}
                title="Instructions"
              />
              <Typography>
                Instructions <span style={{ color: "red" }}>*</span>
              </Typography>
              <Controller
                name="header.instructions"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    minRows={4}
                    placeholder={"e.g.\n1) Answer ALL questions.\n2) Use of calculator is not permitted."}
                    error={!!errors.header?.instructions}
                    helperText={
                      errors.header?.instructions?.message ||
                      `Shown exactly as typed in the Live Preview and the ${getLabel('QUESTION_PAPER')}.`
                    }
                    sx={commonTextFieldStyle}
                  />
                )}
              />
            </Card>

            {/* Parts */}
            <Card sx={cardSx}>
              <SectionHeader
                icon={<ListAltIcon sx={{ color: "#1c97e3", fontSize: "28px" }} />}
                title="Parts / Sections"
                action={
                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                    <Box sx={{ backgroundColor: "#1c97e3", color: "white", px: 1.5, py: 0.5, borderRadius: "8px", fontWeight: 700, fontSize: "12px" }}>{totalMarks} marks</Box>
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openAddPart} sx={{ textTransform: "none", borderRadius: "10px" }}>Add Part</Button>
                  </Box>
                }
              />

              {parts.length === 0 ? (
                <Typography sx={{ color: "#8e9ebc", textAlign: "center", py: 3 }}>No parts yet. Click “Add Part” to begin.</Typography>
              ) : (
                parts.map((p, idx) => (
                  <Card key={idx} sx={{ p: 1.5, mb: 1.5, borderRadius: "10px", boxShadow: "none", border: "1px solid #f1f3f6" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>{p.title}</Typography>
                      <Chip size="small" label={patternLabel(p.pattern)} sx={{ height: 20, fontSize: 10, fontWeight: 700, backgroundColor: p.pattern === "list" ? "#dbeafe" : "#fce7f3", color: p.pattern === "list" ? "#1d4ed8" : "#be185d" }} />
                      {p.choice && <Chip size="small" label="Choice" sx={{ height: 20, fontSize: 10, fontWeight: 700, backgroundColor: "#fef3c7", color: "#92400e" }} />}
                      <Box sx={{ ml: "auto", display: "flex", gap: 0.5 }}>
                        <IconButton size="small" onClick={() => openEditPart(idx)}><EditIcon sx={{ fontSize: 16, color: "#1c97e3" }} /></IconButton>
                        <IconButton size="small" disabled={idx === 0} onClick={() => movePart(idx, -1)}><ArrowUpwardIcon sx={{ fontSize: 16 }} /></IconButton>
                        <IconButton size="small" disabled={idx === parts.length - 1} onClick={() => movePart(idx, 1)}><ArrowDownwardIcon sx={{ fontSize: 16 }} /></IconButton>
                        <IconButton size="small" onClick={() => deletePart(idx)}><DeleteOutlineIcon sx={{ fontSize: 16, color: "#d32f2f" }} /></IconButton>
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: "12px", color: "#5a6473", mt: 0.5 }}>
                      {p.count} {p.pattern === "either-or" ? "pairs" : "questions"} × {p.marks} marks = {partTotal(p)} marks{p.start ? ` · Starts Q${p.start}` : ""}
                    </Typography>
                    {p.choice && <Typography sx={{ fontSize: "11px", color: "#92400e" }}>Choice: {choiceText(p.choice)}</Typography>}
                    {p.splitMark?.length > 0 && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                        {p.splitMark.map((m, mi) => (
                          <Chip key={mi} size="small" label={`${m}`} sx={{ height: 18, fontSize: 10, backgroundColor: "#eef1f5", color: "#5a6473" }} />
                        ))}
                      </Box>
                    )}
                  </Card>
                ))
              )}
            </Card>
          </Box>

          {/* RIGHT */}
          <Box sx={{ flex: "1 1 380px", minWidth: 320, display: "flex", flexDirection: "column", gap: 2 }}>
            <Card sx={cardSx}>
              <SectionHeader
                icon={<VisibilityOutlinedIcon sx={{ color: "#1c97e3", fontSize: "28px" }} />}
                title="Live Preview"
              />
              {renderPreview()}
            </Card>
          </Box>
        </Box>

        {/* Footer actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => navigate("/templates")}
            sx={{ textTransform: "none", borderRadius: "12px", px: 3 }}
          >
            Cancel
          </Button>
          {!isViewMode && (
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              className="gradient-button"
              sx={{ textTransform: "none", borderRadius: "12px", px: 4, fontWeight: 600 }}
            >
              <Typography sx={{ color: "white" }}>{isEditMode ? `Update ${getLabel('TEMPLATE')}` : `Save ${getLabel('TEMPLATE')}`}</Typography>
            </Button>
          )}
        </Box>
      </form>

      {/* Part editor modal */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editingIndex == null ? "Add Part" : "Edit Part"}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
            <Box sx={{ width: "calc(50% - 10px)" }}>
              <Typography>Title <span style={{ color: "red" }}>*</span></Typography>
              <TextField
                fullWidth
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                error={!!draftErrors.title}
                helperText={draftErrors.title}
                sx={modalFieldStyle}
              />
            </Box>
            <Box sx={{ width: "calc(50% - 10px)" }}>
              <Typography>Pattern <span style={{ color: "red" }}>*</span></Typography>
              <Autocomplete
                options={PATTERN_OPTIONS} value={draft.pattern} disableClearable getOptionLabel={patternLabel}
                onChange={(_, v) => setDraft({ ...draft, pattern: v })}
                renderInput={(params) => <TextField {...params} sx={modalFieldStyle} />}
              />
            </Box>
            <Box sx={{ width: "calc(50% - 10px)" }}>
              <Typography>Mark Per Question <span style={{ color: "red" }}>*</span></Typography>
              <TextField
                fullWidth
                type="number"
                value={draft.marks}
                onChange={(e) => {
                  const m = e.target.value;
                  // Drop any selected split values that no longer fit the new mark.
                  setDraft((d) => ({
                    ...d,
                    marks: m,
                    splitMark: (d.splitMark || []).filter((x) => Number(x) <= (Number(m) || 0)),
                  }));
                }}
                error={!!draftErrors.marks}
                helperText={draftErrors.marks}
                sx={modalFieldStyle}
              />
            </Box>
            <Box sx={{ width: "calc(50% - 10px)" }}>
              <Typography>Split Mark</Typography>
              <Select
                fullWidth
                displayEmpty
                value=""
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (v) setDraft((d) => ({ ...d, splitMark: [...(d.splitMark || []), v] }));
                }}
                renderValue={() =>
                  (draft.splitMark || []).length === 0 ? (
                    <span style={{ color: "#9aa3b2" }}>
                      {!Number(draft.marks)
                        ? "Enter Mark per Question first"
                        : `Add split (${splitRemaining} left)`}
                    </span>
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {(draft.splitMark || []).map((m, i) => (
                        <Chip
                          key={i}
                          size="small"
                          label={`${romanLabel(i)}) ${m}`}
                          onMouseDown={(e) => e.stopPropagation()}
                          onDelete={() =>
                            setDraft((d) => ({
                              ...d,
                              splitMark: d.splitMark.filter((_, idx) => idx !== i),
                            }))
                          }
                          sx={{ height: 22, fontSize: 11 }}
                        />
                      ))}
                    </Box>
                  )
                }
                sx={modalFieldStyle}
              >
                {splitAddOptions.length === 0 ? (
                  <MenuItem disabled value="">
                    {Number(draft.marks) ? "Fully split" : "Enter Mark per Question first"}
                  </MenuItem>
                ) : (
                  splitAddOptions.map((o) => (
                    <MenuItem key={o} value={o}>{o}</MenuItem>
                  ))
                )}
              </Select>
              {(draft.splitMark || []).length > 0 && (
                <Typography
                  sx={{ fontSize: "11px", mt: 0.5, color: splitMismatch ? "#d32f2f" : "#16a34a" }}
                >
                  Split total: {splitSum} / {Number(draft.marks) || 0}
                  {splitMismatch ? " — must equal Mark per Question" : " ✓"}
                </Typography>
              )}
            </Box>
            <Box sx={{ width: "calc(50% - 10px)" }}>
              <Typography>{draft.pattern === "either-or" ? "No. of Pairs" : "No. of Questions"} <span style={{ color: "red" }}>*</span></Typography>
              <TextField
                fullWidth
                type="number"
                value={draft.count}
                onChange={(e) => {
                  const c = e.target.value;
                  // When choice-based, Total Given mirrors No. of Questions automatically.
                  setDraft((d) => ({
                    ...d,
                    count: c,
                    ...(d.hasChoice ? { choice: { ...d.choice, total_given: c } } : {}),
                  }));
                }}
                sx={modalFieldStyle}
              />
            </Box>
            <Box sx={{ width: "calc(50% - 10px)" }}>
              <Typography>Start Number</Typography>
              <TextField fullWidth type="number" placeholder="auto" value={draft.start} onChange={(e) => setDraft({ ...draft, start: e.target.value })} sx={modalFieldStyle} />
            </Box>
            <Box sx={{ width: "100%" }}>
              <Typography>Instructions (one per line)</Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                value={draft.instructions}
                onChange={(e) => setDraft({ ...draft, instructions: e.target.value })}
                error={!!draftErrors.instructions}
                helperText={draftErrors.instructions}
                sx={modalFieldStyle}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={<Switch checked={draft.hasChoice} onChange={(e) => {
              const on = e.target.checked;
              // On enabling choice, auto-bind Total Given to current No. of Questions.
              setDraft((d) => ({
                ...d,
                hasChoice: on,
                ...(on ? { choice: { ...d.choice, total_given: d.count } } : {}),
              }));
            }} />}
            label={<Typography sx={{ fontSize: "14px" }}>Choice-based questions</Typography>}
          />
          {draft.hasChoice && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5, mt: 1 }}>
              <Box sx={{ width: "calc(33.33% - 14px)" }}>
                <Typography>Total Given</Typography>
                {/* Auto-bound to No. of Questions for choice-based parts. */}
                <TextField
                  fullWidth
                  type="number"
                  value={draft.choice.total_given}
                  disabled
                  sx={modalFieldStyle}
                />
              </Box>
              <Box sx={{ width: "calc(33.33% - 14px)" }}>
                <Typography>Must Answer</Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={draft.choice.division === "per-group" ? perGroupMustAnswer(draft) : draft.choice.must_answer}
                  onChange={(e) => setChoice({ must_answer: e.target.value })}
                  disabled={draft.choice.division === "per-group"}
                  inputProps={{ min: 1 }}
                  error={!!draftErrors.must_answer}
                  helperText={draftErrors.must_answer}
                  sx={modalFieldStyle}
                />
              </Box>
              <Box sx={{ width: "calc(33.33% - 14px)" }}>
                <Typography>Division</Typography>
                <Autocomplete
                  options={DIVISION_OPTIONS} value={draft.choice.division} disableClearable getOptionLabel={divisionLabel}
                  onChange={(_, v) => setChoice({ division: v })}
                  renderInput={(params) => <TextField {...params} sx={modalFieldStyle} />}
                />
              </Box>
              {draft.choice.division === "per-group" && (
                <>
                  <Box sx={{ width: "calc(50% - 10px)" }}>
                    <Typography>Questions per Group</Typography>
                    <TextField
                      fullWidth
                      type="number"
                      value={draft.choice.group_size}
                      onChange={(e) => setChoice({ group_size: e.target.value })}
                      inputProps={{ min: 1 }}
                      error={!!draftErrors.group_size}
                      helperText={draftErrors.group_size}
                      sx={modalFieldStyle}
                    />
                  </Box>
                  <Box sx={{ width: "calc(50% - 10px)" }}>
                    <Typography>Answer per Group</Typography>
                    <TextField
                      fullWidth
                      type="number"
                      value={draft.choice.group_answer}
                      onChange={(e) => setChoice({ group_answer: e.target.value })}
                      inputProps={{ min: 1 }}
                      error={!!draftErrors.group_answer}
                      helperText={draftErrors.group_answer}
                      sx={modalFieldStyle}
                    />
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" color="secondary" onClick={() => setModalOpen(false)} sx={{ textTransform: "none", borderRadius: "10px" }}>Cancel</Button>
          <Button variant="contained" onClick={savePart} sx={{ textTransform: "none", borderRadius: "10px" }}>Save Part</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateCreate;
